/**
 * AllOrderSourcesPage Container Component
 *
 * Stateless page container that composes section components.
 * State management is handled by the OrderSourceManagement parent.
 */

import { Counter } from '@/components/ui/counter';
import { DataTableShortcuts } from '../../DataTable/DataTableShortcuts';
import { OrderSourcesTabs } from '../Sections/OrderSourcesTabs';
import { OrderSourcesToolbar } from '../Sections/OrderSourcesToolbar';
import { OrderSourcesTable } from '../Sections/OrderSourcesTable';
import { PageTitle } from '../../layouts/Page';
import { useTranslation } from '../../../hooks/useTranslation';
import type { AllOrderSourcesPageProps } from '../types';

const DEFAULT_ORDER_SOURCES: never[] = [];

export function AllOrderSourcesPage({
  currentStatus = 'active',
  totalCount = 0,
  onStatusChange,
  translations,
  isLoading = false,
  isFetching = false,
  orderSources = DEFAULT_ORDER_SOURCES,
  currentPage = 1,
  pageSize = 50,
  onPageChange,
  onPerPageChange,
  onRowClick,
  onCreateNew,
  onStatusToggle,
  sorting,
  onSortingChange,
  canWrite = false,
}: AllOrderSourcesPageProps) {
  const { t } = useTranslation();
  const countText = isLoading ? '...' : totalCount.toLocaleString();

  return (
    <DataTableShortcuts>
      <div className="flex flex-col gap-4 h-full min-h-0 px-6 sm:px-8 lg:px-16">
        <PageTitle>
          <h1 className="font-kapra-expanded text-2xl font-black uppercase leading-[1.5] text-off-black">
            {t('header.title')}
          </h1>
          <Counter value={countText} data-loading={isLoading || isFetching} />
        </PageTitle>

        <div className="flex items-center justify-between gap-4 flex-shrink-0">
          <OrderSourcesTabs
            currentStatus={currentStatus}
            onStatusChange={onStatusChange}
            translations={translations?.tabs}
          />
          <OrderSourcesToolbar
            onCreateNew={onCreateNew}
            canWrite={canWrite}
          />
        </div>

        <OrderSourcesTable
          orderSources={orderSources}
          totalCount={totalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          currentStatus={currentStatus}
          isLoading={isLoading}
          isFetching={isFetching}
          onPageChange={onPageChange}
          onPerPageChange={onPerPageChange}
          onRowClick={onRowClick}
          onStatusToggle={onStatusToggle}
          sorting={sorting}
          onSortingChange={onSortingChange}
          canWrite={canWrite}
          className="flex-1 min-h-0"
        />
      </div>
    </DataTableShortcuts>
  );
}
